// The list of chats (#141): every conversation in every server I'm in, from GET /api/me/chats. Fetched on opening a
// server, on coming back to the tab and every 30 s while it's visible (like serverUnread.ts); the open server's rows
// are then kept live from its socket (chatsLogic.ts liveRows), so only the other servers wait for the poll.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { MyChats } from "@bloom/shared";
import { api } from "./api.ts";
import { rememberAvatars } from "./people.ts";
import { liveRows, mergeChats, type ChatRow, type LiveServer } from "./chatsLogic.ts";

const EVERY_MS = 30_000;

/** `enabled` false (the Classic layout): no requests, no timers. */
export function useMyChats(serverId: string | undefined, enabled = true): { data: MyChats | null; refresh: () => void } {
  const [data, setData] = useState<MyChats | null>(null);
  const live = useRef(true);
  const load = useCallback(() => {
    if (document.visibilityState !== "visible") return;
    api<MyChats>("/me/chats").then(
      (c) => {
        if (!live.current) return;
        rememberAvatars(Object.entries(c.people).map(([id, p]) => ({ id, image: p.image })));
        setData(c);
      },
      () => {},
    );
  }, []);
  useEffect(() => {
    if (!enabled) return;
    live.current = true;
    load();
    const t = setInterval(load, EVERY_MS);
    document.addEventListener("visibilitychange", load);
    window.addEventListener("focus", load);
    return () => {
      live.current = false;
      clearInterval(t);
      document.removeEventListener("visibilitychange", load);
      window.removeEventListener("focus", load);
    };
  }, [serverId, load, enabled]);
  return { data, refresh: load };
}

/** What the list needs from the open server's socket (Server.tsx's snapshot), besides the names. */
export type ChatRowsInput = Omit<LiveServer, "serverId" | "names" | "muted"> & { muted: Set<string> };

/**
 * Every server's rows for the Chats layout: the poll's for the other servers, the open server's rebuilt from its
 * socket so they never wait for it, and a new poll soon when the socket is behind a conversation. `names` is the open
 * server's people plus the polled DM people. With `enabled` false nothing is fetched and the rows are empty.
 */
export function useChatRows(
  enabled: boolean,
  serverId: string,
  input: ChatRowsInput | null,
  serverNames: Map<string, string>,
  polledChats?: MyChats | null,
): { rows: ChatRow[]; names: Map<string, string> } {
  const { data: internalPolled, refresh } = useMyChats(serverId, enabled && !polledChats);
  const polled = polledChats ?? internalPolled;
  const names = useMemo(
    () => new Map([...Object.entries(polled?.people ?? {}).map(([id, p]) => [id, p.name] as [string, string]), ...serverNames]),
    [polled, serverNames],
  );
  const live = useMemo(
    () => (enabled && input?.me && input.channels.length ? liveRows({ ...input, serverId, names }, polled?.chats ?? []) : null),
    // `input` is rebuilt on every render by the caller: depend on its parts.
    [enabled, serverId, input?.me, input?.channels, input?.byChannel, input?.read, input?.mentionCounts, input?.dmClosed, input?.muted, input?.activeId, names, polled],
  );
  const rows = useMemo<ChatRow[]>(() => (enabled ? mergeChats(polled?.chats ?? [], live ? { serverId, rows: live.rows } : null) : []), [enabled, polled, live, serverId]);
  const stale = !!live?.stale;
  useEffect(() => {
    if (!stale) return;
    const t = setTimeout(refresh, 1500);
    return () => clearTimeout(t);
  }, [stale, input?.channels, refresh]);
  return { rows, names };
}
